export const DEFAULT_THEME = {
  primary: "#C9A574",
  secondary: "#F2E5C6",
  accent: "#A48354",
  surface: "#FFFFFF",
  text: "#221C01",
};

export const normalizeHex = (value, fallback = "#000000") => {
  if (!value) return fallback;

  let hex = String(value).trim();

  if (!hex.startsWith("#")) {
    hex = `#${hex}`;
  }

  if (/^#([0-9A-Fa-f]{3})$/.test(hex)) {
    const short = hex.replace("#", "");
    return `#${short
      .split("")
      .map((char) => char + char)
      .join("")}`.toUpperCase();
  }

  if (/^#([0-9A-Fa-f]{6})$/.test(hex)) {
    return hex.toUpperCase();
  }

  return fallback;
};

export const sanitizeTheme = (theme = {}) => {
  return {
    primary: normalizeHex(theme.primary, DEFAULT_THEME.primary),
    secondary: normalizeHex(theme.secondary, DEFAULT_THEME.secondary),
    accent: normalizeHex(theme.accent, DEFAULT_THEME.accent),
    surface: normalizeHex(theme.surface, DEFAULT_THEME.surface),
    text: normalizeHex(theme.text, DEFAULT_THEME.text),
  };
};

const hexToRgb = (hex) => {
  const clean = normalizeHex(hex).replace("#", "");
  const bigint = parseInt(clean, 16);

  return {
    r: (bigint >> 16) & 255,
    g: (bigint >> 8) & 255,
    b: bigint & 255,
  };
};

const getLuminance = ({ r, g, b }) => {
  const values = [r, g, b].map((value) => {
    const channel = value / 255;
    return channel <= 0.03928
      ? channel / 12.92
      : ((channel + 0.055) / 1.055) ** 2.4;
  });

  return 0.2126 * values[0] + 0.7152 * values[1] + 0.0722 * values[2];
};

export const getContrastText = (hex) => {
  const rgb = hexToRgb(hex);
  const luminance = getLuminance(rgb);
  return luminance > 0.5 ? "#221C01" : "#FFFFFF";
};

export const createThemeStyle = (theme = {}) => {
  const safeTheme = sanitizeTheme(theme);

  return {
    "--theme-primary": safeTheme.primary,
    "--theme-secondary": safeTheme.secondary,
    "--theme-accent": safeTheme.accent,
    "--theme-surface": safeTheme.surface,
    "--theme-text": safeTheme.text,
    "--theme-on-primary": getContrastText(safeTheme.primary),
    "--theme-on-secondary": getContrastText(safeTheme.secondary),
    "--theme-on-accent": getContrastText(safeTheme.accent),
  };
};